import { useNavigate, useLocation } from "react-router-dom";
import KakaoTalk from "../../assets/images/payment/kakao_LOGO-07.png";
import {
  KAKAO_CHANNEL_URL,
  KAKAO_CHANNEL_NAME,
  PREMIUM_MANSEOL_PRICE,
} from "../../utils/constants";
import { convertTimeToTwelveTime } from "../../utils/timeConvert";

interface Step4CompleteProps {
  paymentData: {
    phoneNumber: string;
    email?: string;
    paymentMethod: "card" | "bank";
  };
  fortuneData: {
    name: string;
    birth: string;
    time: string;
    leap: string;
    gender: string;
    question: string;
  };
}

// leap, gender 변환 유틸
function getLeapText(leap: string) {
  if (leap === "SOLAR") return "양력";
  if (leap === "LUNAR") return "음력";
  if (leap === "LEAP") return "윤달";
  return leap || "-";
}
function getGenderText(gender: string) {
  if (gender === "MALE") return "남";
  if (gender === "FEMALE") return "여";
  return gender || "-";
}

// 사주 정보 요약 컴포넌트
function SajuInfoBox({
  name,
  sajuInfo,
  leapText,
  genderText,
}: {
  name: string;
  sajuInfo: string;
  leapText: string;
  genderText: string;
}) {
  return (
    <div className="gap-2 py-2 border-b-[0.5px] border-[#0F0C1640]">
      <span className="font-extrabold w-20">사주 정보</span>
      <div className="flex gap-2">
        <div>
          <div className="w-24 text-right font-bold text-sm">
            <p>이름 :</p>
            <p>년월일시 :</p>
            <p>양,음,윤 :</p>
            <p>성별 :</p>
          </div>
        </div>
        <div className="flex flex-col text-sm">
          <p>{name}</p>
          <p>{sajuInfo}</p>
          <p>{leapText}</p>
          <p>{genderText}</p>
        </div>
      </div>
    </div>
  );
}

// 결제 정보 요약 컴포넌트
function PaymentInfoBox({
  paymentData,
}: {
  paymentData: Step4CompleteProps["paymentData"];
}) {
  return (
    <div className="flex gap-2 py-2 border-b-[0.5px] border-[#0F0C1640]">
      <div>
        <span className="font-extrabold w-20">결제 정보</span>
        <div className="w-24 text-right font-bold text-sm">
          <p>금액 :</p>
          <p>휴대폰 :</p>
          <p>이메일 :</p>
        </div>
      </div>
      <div className="flex flex-col gap-1 text-sm">
        <span>
          {paymentData.paymentMethod === "bank" ? "계좌이체" : "카드결제"}
        </span>
        <div>
          <p>{PREMIUM_MANSEOL_PRICE.toLocaleString()}원</p>
          <p>
            {paymentData.phoneNumber.replace(
              /(\d{3})(\d{4})(\d{4})/,
              "$1-$2-$3"
            )}
          </p>
          <p>{paymentData.email || "입력되지 않음"}</p>
        </div>
      </div>
    </div>
  );
}

// 문의 정보 컴포넌트
function InquiryBox() {
  return (
    <div className="flex items-start py-2">
      <span className="font-extrabold w-24">문의</span>
      <a
        href={KAKAO_CHANNEL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-start gap-2"
      >
        <img src={KakaoTalk} alt="카카오톡채널" className="w-12 h-12 -m-2" />
        <span className="text-sm leading-[120%]">
          카카오톡 채널
          <br />'{KAKAO_CHANNEL_NAME}'
        </span>
      </a>
    </div>
  );
}

function Step4Complete({ paymentData, fortuneData }: Step4CompleteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isVideoRoute = location.pathname.includes("/video");
  const handleGoToMain = () => navigate("/payment");

  const sajuInfo = `${fortuneData.birth}${
    fortuneData.time ? ` ${convertTimeToTwelveTime(fortuneData.time)}` : ""
  }`;
  const leapText = getLeapText(fortuneData.leap);
  const genderText = getGenderText(fortuneData.gender);

  return (
    <main className="flex-1 flex flex-col items-center bg-[#F9F9FC] mx-auto">
      <h2 className="text-2xl font-bold mb-8">결제 완료</h2>
      {isVideoRoute ? (
        <div className="text-center text-base mb-4">
          결제가 완료되었습니다.
          <br />
          이메일에 24시간 이내로 사주 영상이 전송될 예정입니다.
        </div>
      ) : (
        <div className="text-center text-base mb-4">
          상담하고 싶은 시간을 <span className="font-bold">카카오톡 채널</span>
          로 보내주세요
          <br />
          <span className="text-xs text-[#666]">
            (카카오톡 채널로 연락을 주셔야 시간 예약이 가능합니다)
          </span>
        </div>
      )}
      <div className="w-full bg-[#F3F3F3] max-w-[320px] rounded-md shadow-md px-5 py-4 mb-8 text-base text-[#222] flex flex-col border border-[#eee]">
        {paymentData.paymentMethod === "bank" && (
          <div className="flex py-2 border-b-[0.5px] border-[#0F0C1640]">
            <span className="font-extrabold w-28">입금 계좌 안내</span>
            <div className="text-sm">
              <p>우리은행</p>
              <p>1002-448-341149(이규호)</p>
            </div>
          </div>
        )}
        <SajuInfoBox
          name={fortuneData.name}
          sajuInfo={sajuInfo}
          leapText={leapText}
          genderText={genderText}
        />
        <PaymentInfoBox paymentData={paymentData} />
        <InquiryBox />
      </div>
      {/* 전화상담 예약 버튼 */}
      {!isVideoRoute && (
        <button
          className="w-full max-w-[320px] h-12 rounded-lg bg-[#B39DDB] text-white text-xl font-extrabold mb-3 shadow-md hover:brightness-95 transition-all"
          onClick={() => window.open(KAKAO_CHANNEL_URL, "_blank")}
        >
          전화상담 예약하기
        </button>
      )}
      {/* 처음으로 돌아가기 버튼 isVideoRoute 인경우 배경색  B39DDB으로 설정, 텍스트도 흰색으로 설정   */}
      <button
        className={`w-full max-w-[320px] h-12 rounded-lg text-xl font-extrabold border shadow-sm hover:bg-[#ede6ff] transition-all ${
          isVideoRoute
            ? "bg-[#B39DDB] hover:bg-[#B39DDB]/80 text-white"
            : "bg-[#F3EDFF] border-[#9D7FDE] text-[#222] hover:bg-[#ede6ff]"
        }`}
        onClick={handleGoToMain}
      >
        처음으로 돌아가기
      </button>
    </main>
  );
}

export default Step4Complete;
